// ABOUTME: Confirmation dialog component for deleting behavior records
// ABOUTME: Displays behavior details and requires user confirmation before deletion

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert
} from '@mui/material';
import { formatRelativeTime } from '../utils/timeUtils';
import { BehaviorType } from '../types/api';
import { getBehaviorIcon, getBehaviorLabel } from '../utils/behaviorUtils';

interface BehaviorDeleteDialogProps {
  open: boolean;
  behavior: {
    type: BehaviorType;
    description: string;
    createdAt: string;
    loggedBy: string;
  } | null;
  onClose: () => void;
  onConfirm: () => void;
}

const BehaviorDeleteDialog: React.FC<BehaviorDeleteDialogProps> = ({
  open,
  behavior,
  onClose,
  onConfirm
}) => {
  if (!behavior) return null;

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Prevent closing via backdrop click or escape key
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
        onClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Delete Behavior Record</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          This action cannot be undone. Are you sure you want to delete this behavior record?
        </Alert>
        
        <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            {getBehaviorIcon(behavior.type, { sx: { fontSize: 24 } })}
            <Typography variant="subtitle1" fontWeight="medium">
              {getBehaviorLabel(behavior.type)} Behavior
            </Typography>
          </Box>
          
          {behavior.description && (
            <Typography variant="body2" color="text.secondary" mb={1}>
              <strong>Description:</strong> {behavior.description}
            </Typography>
          )}
          
          <Typography variant="body2" color="text.secondary" mb={0.5}>
            <strong>Logged by:</strong> {behavior.loggedBy}
          </Typography>
          
          <Typography variant="body2" color="text.secondary">
            <strong>Date:</strong> {formatRelativeTime(behavior.createdAt)}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button 
          onClick={onConfirm} 
          variant="contained" 
          color="error"
        >
          Delete Record
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BehaviorDeleteDialog;